// @flow
import BaseTracker from "./BaseTracker";
import {roundAndScale} from "../mathUtils";
import type {Product} from "../../models/Product";
import type {Item} from "../../models/Item";
import type {OrderReducerState} from "../../reducers/orderReducer";
import type {CouponReducerState} from "../../reducers/couponReducer";

export const ITEM_CATEGORY = "Accessory Navigator";

const couponCode = (coupon: ?CouponReducerState): ?string =>
    (coupon && coupon.coupon && coupon.coupon.coupon && coupon.coupon.coupon.couponNo)
        ? coupon.coupon.coupon.couponNo.index
        : undefined;

// keys with undefined values are stripped so events never carry e.g.
// "currency: undefined" — either a parameter is sent with a value or not at all
const withoutUndefined = (payload: {}) => {
    const cleaned = {};
    Object.keys(payload).forEach(key => {
        if (payload[key] !== undefined) cleaned[key] = payload[key];
    });
    return cleaned;
};

const productItem = (product: Product, quantity: number = 1) => withoutUndefined({
    item_id: product.articleNumber,
    item_name: product.name,
    affiliation: product.dealerId,
    item_category: ITEM_CATEGORY,
    price: product.price ? product.price.value : undefined,
    quantity,
});

const productValue = (product: Product): ?number => (product.price ? product.price.value : undefined);

const productCurrency = (product: Product): ?string => (product.price ? product.price.currency : undefined);

const cartItems = (items: Item[]) => items.map(item => productItem(item.product, item.amount));

// value and currency are only sent together: Google requires a currency
// whenever value is set, and lead-only dealers have no prices at all
const cartValue = (items: Item[]): ?number => {
    const priced = items.filter(item => item.product.price);
    if (!priced.length) return undefined;
    return roundAndScale(priced.reduce((sum, item) => sum + item.product.price.value * item.amount, 0), 2);
};

const cartCurrency = (items: Item[]): ?string => {
    const priced = items.find(item => item.product.price);
    return priced ? priced.product.price.currency : undefined;
};

const orderItems = (order: OrderReducerState) => order.order.orderItems.map(item => ({
    item_id: item.product.productNo,
    item_name: item.product.productName,
    affiliation: order.order.dealerId,
    item_category: ITEM_CATEGORY,
    price: item.product.unitPrice.pricePerUnit.cost,
    quantity: item.amount.amount,
}));

/**
 * Shared Google tracking (GA4 events, https://developers.google.com/tag-platform/gtagjs/reference/events).
 * Builds every event payload exactly once so the GA4 and the Tag Manager
 * variant are guaranteed to report the same data; subclasses only provide
 * the transport via send().
 */
export default class GoogleBaseTracker extends BaseTracker {
    // eslint-disable-next-line no-unused-vars
    send(eventName: string, payload: ?{}): void {
        throw new Error("not implemented");
    }

    sendEvent(eventName: string, payload: {}) {
        this.send(eventName, withoutUndefined(payload));
    }

    logSelectContent(contentType: string, contentId: string) {
        this.sendEvent("select_content", {
            content_type: contentType,
            content_id: String(contentId).trim().replace(/\s+/g, "_"),
        });
    }

    logSearch(searchPhrase: string) {
        this.sendEvent("search", {
            search_term: searchPhrase,
        });
    }

    showProductDetails(product: Product) {
        this.sendEvent("view_item", {
            currency: productCurrency(product),
            value: productValue(product),
            items: [productItem(product)],
        });
    }

    logShare(product: Product) {
        this.sendEvent("share", {
            item_id: product.articleNumber,
        });
    }

    logGenerateLead(products: Product[], leadSource: string) {
        const items = products.map(product => ({product, amount: 1}));
        this.sendEvent("generate_lead", {
            currency: cartCurrency(items),
            value: cartValue(items),
            lead_source: leadSource,
            items: cartItems(items),
        });
    }

    insertToWatchlist(product: Product) {
        this.sendEvent("add_to_wishlist", {
            currency: productCurrency(product),
            value: productValue(product),
            items: [productItem(product)],
        });
    }

    addItem(product: Product, amount: number = 1) {
        const value = productValue(product);
        this.sendEvent("add_to_cart", {
            currency: productCurrency(product),
            value: value != null ? roundAndScale(value * amount, 2) : undefined,
            items: [productItem(product, amount)],
        });
    }

    removeItem(product: Product, amount: number = 1) {
        const value = productValue(product);
        this.sendEvent("remove_from_cart", {
            currency: productCurrency(product),
            value: value != null ? roundAndScale(value * amount, 2) : undefined,
            items: [productItem(product, amount)],
        });
    }

    logViewCart(items: Item[]) {
        this.sendEvent("view_cart", {
            currency: cartCurrency(items),
            value: cartValue(items),
            items: cartItems(items),
        });
    }

    logBeginCheckout(items: Item[], coupon: ?CouponReducerState) {
        this.sendEvent("begin_checkout", {
            currency: cartCurrency(items),
            value: cartValue(items),
            coupon: couponCode(coupon),
            items: cartItems(items),
        });
    }

    logAddShippingInfo(items: Item[], coupon: ?CouponReducerState) {
        this.sendEvent("add_shipping_info", {
            currency: cartCurrency(items),
            value: cartValue(items),
            coupon: couponCode(coupon),
            items: cartItems(items),
        });
    }

    logAddPaymentInfo(items: Item[], coupon: ?CouponReducerState, paymentType: ?string) {
        this.sendEvent("add_payment_info", {
            currency: cartCurrency(items),
            value: cartValue(items),
            coupon: couponCode(coupon),
            payment_type: paymentType,
            items: cartItems(items),
        });
    }

    logOrder(order: OrderReducerState, coupon: ?CouponReducerState) {
        const shipping = roundAndScale(order.orderItemsPriceSumWithShipping.cost - order.orderItemsPriceSum.cost, 2);
        // affiliation lives on the items, not on the purchase event itself
        this.sendEvent("purchase", {
            transaction_id: order.order.orderNo,
            coupon: couponCode(coupon),
            shipping,
            currency: order.totalBillingAmount.currency.code,
            // goods value as billed (incl. coupon discount), excluding shipping
            value: roundAndScale(Math.max(0, order.totalBillingAmount.cost - shipping), 2),
            payment_type: (order.payment || {}).paymentProvider,
            items: orderItems(order),
        });
    }
}
